import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Col,
  DatePicker,
  Form,
  Row,
  Spin,
  Tabs,
  Typography,
} from 'antd';
import moment from 'moment';
import API from 'utils/api';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import currencyFormatter from 'utils/currencyFormatter';

const { TabPane } = Tabs;
const { Text } = Typography;

const URI_AVANCE = 'presupuestos/avance-presupuestal-de-nomina/';

export const permissionAvancePresupuestalNomina = {
  permissionModel: 'presupuestoanualdeegreso',
};

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

// Abreviaturas del requerimiento para los títulos de las quincenas (Q1 ENE, Q2 ENE, ...)
const MESES_CORTOS = [
  'ENE', 'FEB', 'MZO', 'ABR', 'MAY', 'JUN', 'JUL', 'AGT', 'SEP', 'OCT', 'NOV', 'DIC',
];

const redondear = (valor) => Math.round(valor * 100) / 100;

const renderImporte = (valor) => (
  valor === null || valor === undefined ? '' : currencyFormatter(valor)
);

// Disponible negativo = se pagó más de lo vigente (sobre-ejercido)
const renderDisponible = (valor) => (
  valor < 0 ? <Text type="danger">{renderImporte(valor)}</Text> : renderImporte(valor)
);

const columnaImporte = (titleText, dataIndex, width = 170) => ({
  titleText,
  dataIndex,
  key: dataIndex,
  width,
  align: 'right',
  render: dataIndex === 'disponible' ? renderDisponible : renderImporte,
});

// Vacío si la columna no tiene importes (meses posteriores al de consulta)
const sumar = (filas, campo) => (
  filas.every((fila) => fila[campo] === null || fila[campo] === undefined)
    ? null
    : redondear(filas.reduce((total, fila) => total + (fila[campo] || 0), 0))
);

/**
 * Fila de totales: "Total" en la primera columna y la suma de cada columna de importe.
 * @param {object[]} filas
 * @param {string[]} campos dataIndex de las columnas de importe, en orden
 * @param {number} columnasDeTexto columnas antes de los importes
 */
const totales = (filas, campos, columnasDeTexto) => campos.map((campo, idx) => ({
  ...(idx === 0 ? { label: 'Total', labelAlign: 'right', labelColSpan: columnasDeTexto } : {}),
  value: sumar(filas, campo),
  valueAlign: 'right',
  render: campo === 'disponible' ? renderDisponible : renderImporte,
}));

/** Mes por omisión: el actual si es del ejercicio seleccionado; si no, diciembre del ejercicio. */
const periodoPorOmision = (ejercicio) => {
  const hoy = moment().startOf('month');
  if (!ejercicio || Number(ejercicio) === hoy.year()) return hoy;
  return moment(`${ejercicio}-12-01`, 'YYYY-MM-DD');
};

const AvancePresupuestalNomina = ({ permission }) => {
  const periodoFiscalSelected = useSelector(({ auth }) => auth.periodoFiscalSelected);
  const [periodo, setPeriodo] = useState(() => periodoPorOmision(periodoFiscalSelected?.periodo));
  const [tab, setTab] = useState('anual');
  const [loading, setLoading] = useState(false);
  const [claves, setClaves] = useState([]);
  const [cuentas, setCuentas] = useState([]);

  useEffect(() => {
    setPeriodo(periodoPorOmision(periodoFiscalSelected?.periodo));
  }, [periodoFiscalSelected?.periodo]);

  useEffect(() => {
    let mounted = true;
    const fetchAvance = async () => {
      try {
        setLoading(true);
        const { data } = await API.get(URI_AVANCE, {
          params: { anio: periodo.year(), mes: periodo.month() + 1 },
        });
        if (mounted) {
          setClaves(data?.claves || []);
          setCuentas(data?.cuentas || []);
        }
      } catch (err) {
        onError(err);
        if (mounted) {
          setClaves([]);
          setCuentas([]);
        }
      }
      if (mounted) setLoading(false);
    };
    fetchAvance();
    return () => { mounted = false; };
  }, [periodo]);

  const columnaCuenta = {
    titleText: 'Cuenta Contable',
    dataIndex: 'cuenta_contable',
    key: 'cuenta_contable',
    width: 380,
  };

  const columnasAnual = [
    {
      titleText: 'Clave Presupuestal',
      dataIndex: 'clave_presupuestal',
      key: 'clave_presupuestal',
      width: 380,
      // Lo pagado en nómina que no corresponde a ninguna clave autorizada
      render: (val, record) => (record?.sin_clave ? <Text type="warning">{val}</Text> : val),
    },
    columnaCuenta,
    columnaImporte('Aprobado', 'aprobado'),
    columnaImporte('Modificaciones', 'modificaciones'),
    columnaImporte('Vigente', 'vigente'),
    columnaImporte('Pagado', 'pagado'),
    columnaImporte('Disponible', 'disponible'),
  ];

  const columnasMensual = [
    columnaCuenta,
    columnaImporte('Anual', 'aprobado'),
    columnaImporte('Vigente', 'vigente'),
    ...MESES.map((mes, idx) => columnaImporte(mes, `mes_${idx + 1}`, 150)),
    columnaImporte('Total Pagado', 'pagado'),
    columnaImporte('Total Disponible', 'disponible'),
  ];

  const columnasQuincenal = [
    columnaCuenta,
    columnaImporte('Anual', 'aprobado'),
    columnaImporte('Vigente', 'vigente'),
    ...MESES_CORTOS.flatMap((mes, idx) => [
      columnaImporte(`Q1 ${mes}`, `quincena_${idx * 2 + 1}`, 140),
      columnaImporte(`Q2 ${mes}`, `quincena_${idx * 2 + 2}`, 140),
    ]),
    columnaImporte('Total Pagado', 'pagado'),
    columnaImporte('Total Disponible', 'disponible'),
  ];

  const pestanas = [
    {
      key: 'anual', titulo: 'Anual', columnas: columnasAnual, filas: claves,
    },
    {
      key: 'pagado_mensual', titulo: 'Pagado Mensual', columnas: columnasMensual, filas: cuentas,
    },
    {
      key: 'pagado_quincenal',
      titulo: 'Pagado Quincenal',
      columnas: columnasQuincenal,
      filas: cuentas,
    },
  ];

  return (
    <Row className="container">
      <Col span={24}>
        <Spin tip="Cargando..." spinning={loading}>
          <Form layout="vertical">
            <Row gutter={10} align="bottom">
              <Col xs={24} sm={12} md={6}>
                <Form.Item label="Periodo de Consulta">
                  <DatePicker
                    picker="month"
                    format="MM/YYYY"
                    allowClear={false}
                    value={periodo}
                    onChange={(valor) => valor && setPeriodo(valor.startOf('month'))}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={18}>
                <Form.Item>
                  <Text type="secondary">
                    {`Acumulado de enero a ${MESES[periodo.month()].toLowerCase()} `
                      + `de ${periodo.year()}`}
                  </Text>
                </Form.Item>
              </Col>
            </Row>
          </Form>
          <Tabs type="card" activeKey={tab} onChange={setTab}>
            {pestanas.map(({
              key, titulo, columnas, filas,
            }) => {
              const campos = columnas
                .filter((col) => col.align === 'right')
                .map((col) => col.dataIndex);
              return (
                <TabPane tab={titulo} key={key}>
                  <Table
                    cols={columnas}
                    data={filas}
                    rowKey="id"
                    permission={permission}
                    summaryCells={totales(filas, campos, columnas.length - campos.length)}
                  />
                </TabPane>
              );
            })}
          </Tabs>
        </Spin>
      </Col>
    </Row>
  );
};

AvancePresupuestalNomina.propTypes = {
  permission: PropTypes.shape({}),
};

AvancePresupuestalNomina.defaultProps = {
  permission: {},
};

export default AvancePresupuestalNomina;
